import type { StepTypeKebabCase } from 'src/types/course/step';
import { componentNameSnakeCase } from 'shared/composables/strapi/useComponent';
import { stackKindOf } from './utilities/stackKindOf';

type StackItemParams = { populate: string | object };
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Params = Record<string, any>;

/** The drill kinds a step (and a filter step's drill) can be. */
const DRILL_KINDS = [
  'vocabulary',
  'pairs',
  'single-choice',
  'multi-choice',
  'fill-in-the-blank',
  'cross-match',
  'true-false',
  'word-builder',
  'memory-flip',
  'sentence-builder',
] as const satisfies readonly StepTypeKebabCase[];

/** Top-level step fields. `type` is the filter's target kind (stage 1 used to bring it). */
const STEP_FIELDS = ['step_name', 'instructions', 'step_category', 'function'];

const getDrill = (drillKinds: readonly StepTypeKebabCase[]) => ({
  populate: {
    drill_type: {
      on: Object.fromEntries(
        drillKinds.map((kind) => [`drill.${kind}`, { populate: '*' }]),
      ),
    },
    settings: {
      populate: '*',
    },
    filter_settings: {
      populate: '*',
    },
  },
});

/** The stack of a step of type `kind` (a True / False step: a Vocabulary stack). */
const getStack = (
  kind: StepTypeKebabCase,
  stackItemParams: StackItemParams,
) => {
  const stackKind = stackKindOf(kind);
  return {
    populate: {
      stack_source: {
        on: {
          [`stack.${stackKind}`]: {
            populate: {
              [`${componentNameSnakeCase(stackKind)}_item`]: stackItemParams,
            },
          },
        },
      },
    },
  };
};

const stackItemParamsOf = (kind: StepTypeKebabCase): StackItemParams =>
  kind === 'fill-in-the-blank'
    ? { populate: { blank_item: { populate: '*' } } }
    : { populate: '*' };

const theoryPopulate = () => ({
  theory_content: {
    on: {
      ['theory.html-content']: {
        populate: '*',
      },
      ['theory.image-content']: {
        populate: {
          image: {
            fields: ['url'],
          },
        },
      },
      ['theory.video-embed-content']: {
        populate: '*',
      },
    },
  },
});

/** The `step_type` dynamic-zone populate of one step component. */
function stepTypeFragment(
  stepType: StepTypeKebabCase,
  filterKinds: readonly StepTypeKebabCase[],
): Params | null {
  switch (stepType) {
    case 'single-choice':
    case 'multi-choice':
    case 'vocabulary':
    case 'pairs':
    case 'cross-match':
    case 'fill-in-the-blank':
    case 'true-false':
    case 'word-builder':
    case 'memory-flip':
    case 'sentence-builder':
      return {
        populate: {
          drill: getDrill([stepType]),
          stack: getStack(stepType, stackItemParamsOf(stepType)),
        },
      };
    case 'filter':
      return filterKinds.length
        ? { populate: { drill: getDrill(filterKinds) } }
        : null;
    case 'theory':
      return { populate: theoryPopulate() };
    default:
      return null;
  }
}

function stepParams(on: Params, fields: string[]): Params {
  return {
    populate: {
      tags: true,
      step_type: { on },
    },
    fields,
  };
}

/**
 * Per-kind params (the former stage-2 request). A filter step needs its
 * target kind (`step.type`) for the drill_type fragment.
 */
export function getStepParams(
  stepTypeKebab?: StepTypeKebabCase,
  filterType?: StepTypeKebabCase,
): Params {
  if (!stepTypeKebab) return {};
  const fragment = stepTypeFragment(
    stepTypeKebab,
    filterType ? [filterType] : [],
  );
  return fragment
    ? stepParams({ [`step.${stepTypeKebab}`]: fragment }, STEP_FIELDS)
    : {};
}

const STEP_KINDS: readonly StepTypeKebabCase[] = [
  ...DRILL_KINDS,
  'filter',
  'theory',
];

/**
 * One GET for any step kind: `step_type.on` lists every step component with
 * exactly its per-kind populate (a filter step's drill lists every drill
 * kind, since its target kind is not known before the response), plus `type`
 * so the filter target kind arrives with the content.
 */
export const UNIVERSAL_STEP_PARAMS: Readonly<Params> = Object.freeze(
  stepParams(
    Object.fromEntries(
      STEP_KINDS.map((kind) => [
        `step.${kind}`,
        stepTypeFragment(kind, DRILL_KINDS),
      ]),
    ),
    [...STEP_FIELDS, 'type'],
  ),
);
